// War-room replay page — login-gated, shareable, read-only.
//
// Reached via `?replay=<id>` deep-link. WebDashboard captures the id
// before the auth wall; after login this component renders in place of
// the normal dashboard until the user clicks Back or navigates away.
//
// Reuses SnapshotSection + WarRoomSeatsRenderer from the team-workspace
// rendering path — no live events, no tether, no dispatch card.

import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, AlertCircle, Search } from 'lucide-react';
import { getReplayDetail, ApiError, type SharedDetail } from '../lib/api';
import { SnapshotSection } from '../teamWorkspace/SharedResourceDetailPage';
import ReplayDataset, { isReplayDatasetSnapshot } from './ReplayDataset';

interface Props {
  replayId: string;
  /** Exits replay view and returns to the normal dashboard. */
  onBack?(): void;
}

/**
 * Renders the snapshot of a curated war-room benchmark, fetched from
 * `GET /replays/:id`. Static and read-only — no live event tail.
 */
export default function ReplayPage({ replayId, onBack }: Props) {
  const { data: detail, isLoading, error } = useQuery<SharedDetail, ApiError>({
    queryKey: ['replay', replayId],
    queryFn: () => getReplayDetail(replayId),
    // Don't retry on definitive HTTP errors — the resource doesn't exist
    // or the caller lacks permission; a retry won't help.
    retry: (failureCount, err) => {
      if (err instanceof ApiError && [401, 403, 404].includes(err.status)) return false;
      return failureCount < 2;
    },
  });

  function renderError(err: unknown) {
    if (err instanceof ApiError && err.status === 404) {
      return (
        <div className="flex flex-col items-center justify-center py-20 space-y-4">
          <Search className="w-12 h-12 text-[#8888a0]" />
          <p className="text-white font-semibold text-lg">Replay not found</p>
          <p className="text-[#8888a0] text-sm max-w-sm text-center leading-relaxed">
            The replay id{' '}
            <span className="font-mono text-[#aaaab8] bg-[#16161e] px-1.5 py-0.5 rounded">
              {replayId}
            </span>{' '}
            does not exist or has been removed.
          </p>
        </div>
      );
    }

    if (err instanceof ApiError && err.status === 403) {
      return (
        <div className="flex flex-col items-center justify-center py-20 space-y-4">
          <AlertCircle className="w-12 h-12 text-red-400" />
          <p className="text-white font-semibold text-lg">Access denied</p>
          <p className="text-[#8888a0] text-sm">
            You do not have permission to view this replay.
          </p>
        </div>
      );
    }

    return (
      <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/30 rounded-lg p-4">
        <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-red-400 font-medium">Failed to load replay</p>
          <p className="text-xs text-[#8888a0] mt-1">
            {err instanceof Error ? err.message : 'Unknown error'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white flex flex-col">
      {/* Sticky header — ATO branding + optional back-to-dashboard affordance */}
      <header className="sticky top-0 z-10 border-b border-[#2a2a3a] bg-[#0a0a0f]/95 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          {onBack && (
            <button
              onClick={onBack}
              className="p-1.5 rounded-md hover:bg-[#2a2a3a]/60 text-[#8888a0] hover:text-white transition-colors shrink-0"
              aria-label="Back to dashboard"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span className="text-sm font-bold text-white tracking-tight shrink-0">ATO</span>
            <span className="text-[#2a2a3a] shrink-0">·</span>
            <span className="text-sm text-[#8888a0] shrink-0">War-room replay</span>
            {detail?.title && (
              <>
                <span className="text-[#2a2a3a] shrink-0">·</span>
                <span className="text-sm text-white truncate font-medium">{detail.title}</span>
              </>
            )}
          </div>
          {/* "Get ATO" CTA — top-right, unobtrusive */}
          <a
            href="https://agentictool.ai"
            target="_blank"
            rel="noreferrer"
            className="shrink-0 hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#00FFB2]/10 border border-[#00FFB2]/30 text-[#00FFB2] text-xs font-medium hover:bg-[#00FFB2]/15 transition-colors"
          >
            Get ATO
          </a>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Loading skeleton — matches the shape SharedResourceDetailPage uses */}
        {isLoading && (
          <div className="space-y-3 animate-pulse">
            <div className="h-32 bg-[#16161e] rounded-lg border border-[#2a2a3a]" />
            <div className="h-48 bg-[#16161e] rounded-lg border border-[#2a2a3a]" />
            <div className="h-48 bg-[#16161e] rounded-lg border border-[#2a2a3a]" />
          </div>
        )}

        {/* Error state */}
        {!isLoading && error && renderError(error)}

        {detail?.encryption_mode === 'plaintext' &&
          (isReplayDatasetSnapshot(detail.snapshot) ? (
            <ReplayDataset replayId={replayId} snapshot={detail.snapshot} />
          ) : (
            <SnapshotSection detail={detail} kind="war-room" />
          ))}
      </main>

      {/* Footer — ATO attribution */}
      <footer className="border-t border-[#16161e] py-5 px-6 mt-auto">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#5a5a6e]">
          <p>
            Powered by{' '}
            <a
              href="https://agentictool.ai"
              target="_blank"
              rel="noreferrer"
              className="text-[#00FFB2] hover:underline"
            >
              ATO
            </a>{' '}
            — these are real, replayable AI receipts.
          </p>
          <div className="flex items-center gap-5">
            <a
              href="https://agentictool.ai"
              target="_blank"
              rel="noreferrer"
              className="hover:text-[#aaaab8] transition-colors"
            >
              agentictool.ai
            </a>
            <a
              href="https://github.com/WillNigri/Agentic-Tool-Optimization#readme"
              target="_blank"
              rel="noreferrer"
              className="hover:text-[#aaaab8] transition-colors"
            >
              Docs
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
